import React, { useState, useEffect } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Badge } from '../components/ui/badge';
import { Plus, FileText, Calendar as CalendarIcon, Loader2, Download, Users } from 'lucide-react';
import { format } from 'date-fns';
import { nl, enUS } from 'date-fns/locale';

import { API_URL } from '../config/api';

export function MeetingMinutes() {
  const { selectedEntity, isDutchEntity } = useEntity();
  const { t, language } = useLanguage();
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedMinutes, setSelectedMinutes] = useState(null);

  const [formData, setFormData] = useState({
    meeting_date: new Date(),
    meeting_type: 'board',
    attendees: '',
    summary_input: ''
  });

  const meetingTypes = isDutchEntity && language === 'nl' ? [
    { value: 'board', label: 'Bestuursvergadering' },
    { value: 'shareholders', label: 'Aandeelhoudersvergadering' },
    { value: 'general', label: 'Algemene Vergadering' }
  ] : [
    { value: 'board', label: 'Board Meeting' },
    { value: 'shareholders', label: 'Shareholder Meeting' },
    { value: 'general', label: 'General Meeting' }
  ];

  useEffect(() => {
    if (selectedEntity) {
      fetchMinutes();
    }
  }, [selectedEntity]);

  const fetchMinutes = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/minutes?entity_id=${selectedEntity.entity_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setMinutes(data);
      }
    } catch (error) {
      console.error('Failed to fetch minutes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGenerating(true);

    try {
      const response = await fetch(`${API_URL}/api/minutes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entity_id: selectedEntity.entity_id,
          meeting_date: formData.meeting_date.toISOString(),
          meeting_type: formData.meeting_type,
          attendees: formData.attendees.split(',').map(a => a.trim()).filter(a => a),
          summary_input: formData.summary_input,
          language: isDutchEntity ? language : 'en'
        })
      });

      if (response.ok) {
        fetchMinutes();
        resetForm();
        setDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to generate minutes:', error);
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      meeting_date: new Date(),
      meeting_type: 'board',
      attendees: '',
      summary_input: ''
    });
  };

  const getMeetingTypeLabel = (type) => {
    const found = meetingTypes.find(t => t.value === type);
    return found?.label || type;
  };

  if (!selectedEntity) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">
          {isDutchEntity ? 'Selecteer eerst een entiteit' : 'Select an entity first'}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('nav.minutes')}
          </h1>
          <p className="text-muted-foreground">{selectedEntity.name}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand" data-testid="new-minutes-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('minutes.newMinutes')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('minutes.newMinutes')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Meeting Date */}
              <div>
                <Label>{t('minutes.meetingDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(formData.meeting_date, 'PPP', { locale: isDutchEntity && language === 'nl' ? nl : enUS })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.meeting_date}
                      onSelect={(date) => date && setFormData({ ...formData, meeting_date: date })}
                      locale={isDutchEntity && language === 'nl' ? nl : enUS}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Meeting Type */}
              <div>
                <Label>{t('minutes.meetingType')}</Label>
                <Select
                  value={formData.meeting_type}
                  onValueChange={(value) => setFormData({ ...formData, meeting_type: value })}
                >
                  <SelectTrigger data-testid="meeting-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {meetingTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Attendees */}
              <div>
                <Label>{t('minutes.attendees')}</Label>
                <Input
                  data-testid="attendees-input"
                  value={formData.attendees}
                  onChange={(e) => setFormData({ ...formData, attendees: e.target.value })}
                  placeholder={isDutchEntity && language === 'nl' ? 'Jan Jansen, Piet Pietersen, ...' : 'John Smith, Jane Doe, ...'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {isDutchEntity && language === 'nl' ? 'Scheid namen met komma\'s' : 'Separate names with commas'}
                </p>
              </div>

              {/* Summary Input */}
              <div>
                <Label>{t('minutes.summary')}</Label>
                <Textarea
                  data-testid="summary-input"
                  value={formData.summary_input}
                  onChange={(e) => setFormData({ ...formData, summary_input: e.target.value })}
                  placeholder={isDutchEntity && language === 'nl' 
                    ? 'Geef een beknopte samenvatting van de vergadering. De AI werkt dit uit tot formele notulen...'
                    : 'Provide a brief summary of the meeting. AI will expand this into formal minutes...'}
                  rows={6}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="flex-1 btn-brand" disabled={generating} data-testid="generate-minutes-btn">
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isDutchEntity && language === 'nl' ? 'Genereren...' : 'Generating...'}
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      {t('minutes.generateMinutes')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Minutes List */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : minutes.length > 0 ? (
        <div className="grid gap-4">
          {minutes.map(m => (
            <Card 
              key={m.minutes_id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${selectedMinutes?.minutes_id === m.minutes_id ? 'ring-2 ring-[hsl(var(--brand))]' : ''}`}
              onClick={() => setSelectedMinutes(selectedMinutes?.minutes_id === m.minutes_id ? null : m)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {getMeetingTypeLabel(m.meeting_type)}
                      </h3>
                      <Badge variant="outline" className={m.status === 'approved' ? 'badge-success' : 'badge-warning'}>
                        {m.status === 'approved' 
                          ? (isDutchEntity && language === 'nl' ? 'Goedgekeurd' : 'Approved')
                          : (isDutchEntity && language === 'nl' ? 'Concept' : 'Draft')}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {new Date(m.meeting_date).toLocaleDateString(isDutchEntity && language === 'nl' ? 'nl-NL' : 'en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      {m.attendees?.join(', ')}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); }}>
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                </div>

                {/* Expanded content */}
                {selectedMinutes?.minutes_id === m.minutes_id && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="minutes-preview">
                      <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                        {m.generated_minutes}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {isDutchEntity && language === 'nl'
                ? 'Nog geen notulen. Maak je eerste notulen aan!'
                : 'No meeting minutes yet. Create your first minutes!'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
